import type { Handoff, HandoffCloser, HandoffSkill } from "@/lib/types";

/**
 * Naming the closer (ADR-031): the setter picks who takes the caller from the closers on the line
 * a handoff would wait in, or narrows "any closer" by a skill. Pure: what the picker shows and what
 * the request carries, with tests, so the dock's form holds no rule of its own.
 */

/** How a caller reaches the closer: on hold from the moment they are sent, or after the setter
 *  has briefed somebody (ADR-027). */
export type HandoffMode = "blind" | "consult";

/** A closer's state on the line, in the setter's words, and whether the ACD would ring them now.
 *  The states are the event-tracker's (`services/shared/state/queues.py`), plus `signed_out` for a
 *  rep the line has no record of. Only a rep who is free can be picked: naming somebody on a call
 *  would wait the ring window for them and then tell the setter they were not free. */
export const CLOSER_STATES: Record<string, { label: string; pickable: boolean }> = {
  available: { label: "Ready", pickable: true },
  ringing: { label: "Phone ringing", pickable: false },
  busy: { label: "On a call", pickable: false },
  disposition: { label: "Wrapping up", pickable: false },
  wrapup: { label: "Wrapping up", pickable: false },
  paused: { label: "On a break", pickable: false },
  dialing: { label: "On another call", pickable: false },
  busy_external: { label: "On another call", pickable: false },
  offline: { label: "Phone offline", pickable: false },
  signed_out: { label: "Not signed in", pickable: false },
};

/** A closer as the setter knows them: their name, or their extension for a phone nobody holds. */
export function closerName(closer: Pick<HandoffCloser, "display_name" | "user">): string {
  return closer.display_name ?? `Extension ${closer.user}`;
}

export interface CloserRow {
  closer: HandoffCloser;
  name: string;
  /** "Ready", "On a call", … */
  status: string;
  pickable: boolean;
}

/**
 * The closers holding every skill in `needs`, those who can be picked first and then by name, so a
 * setter in a hurry finds somebody free at the top. A skill is held at any level: the picker asks
 * for a skill, not a grade.
 */
export function closerRows(
  closers: readonly HandoffCloser[],
  needs: readonly string[],
): CloserRow[] {
  return closers
    .filter((c) => needs.every((slug) => (c.skills[slug] ?? 0) >= 1))
    .map((c) => {
      const state = CLOSER_STATES[c.state] ?? { label: c.state, pickable: false };
      // A closer on My calls only is still picked by name (ADR-037); the row says so, because
      // "Any closer who is free" never rings them.
      const status = c.own_calls_only ? `${state.label} · My calls only` : state.label;
      return { closer: c, name: closerName(c), status, pickable: state.pickable };
    })
    .sort((a, b) => Number(b.pickable) - Number(a.pickable) || a.name.localeCompare(b.name));
}

/**
 * The rows a setter's search matches: by name, by extension, or by a skill's name.
 *
 * Skills are searchable because that is how a setter thinks about a closer they do not know by
 * name — "who speaks Spanish" — and because the chips above the list only offer the skills the
 * line declares, while a closer may hold others.
 */
export function matchClosers(
  rows: readonly CloserRow[],
  search: string,
  skills: readonly HandoffSkill[] = [],
): CloserRow[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...rows];
  const named = new Set(
    skills.filter((s) => s.name.toLowerCase().includes(needle)).map((s) => s.slug),
  );
  return rows.filter(
    (row) =>
      row.name.toLowerCase().includes(needle) ||
      row.closer.user.includes(needle) ||
      Object.keys(row.closer.skills).some((slug) => slug.includes(needle) || named.has(slug)),
  );
}

/** Beyond this many closers the list gets a search box: a shorter one is read at a glance. */
export const CLOSER_SEARCH_FROM = 7;

/**
 * The closers listed who keep to My calls only, as the setter should hear it: a caller reaches them
 * only by name (ADR-037). Null when nobody listed does.
 *
 * Named up to a point. On a line with forty closers on My calls only this was a paragraph of
 * names nobody reads, under a list that already says it on every row.
 */
export function ownCallsHelp(rows: readonly CloserRow[]): string | null {
  const names = rows.filter((row) => row.closer.own_calls_only).map((row) => row.name);
  if (names.length === 0) return null;
  if (names.length > NAMED_IN_HELP) {
    return `${names.length} of these closers take a caller only when picked by name.`;
  }
  const who =
    names.length === 1
      ? `${names[0]} takes`
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} take`;
  return `${who} a caller only when picked by name.`;
}

/** How many of them the line above names before it counts them instead. */
const NAMED_IN_HELP = 4;

export interface SkillChip extends HandoffSkill {
  /** Closers on the line holding it, so a chip nobody holds can say so. */
  holders: number;
}

/** The skills the setter may narrow by, with how many of the listed closers hold each. */
export function skillChips(
  skills: readonly HandoffSkill[],
  closers: readonly HandoffCloser[],
): SkillChip[] {
  return skills.map((skill) => ({
    ...skill,
    holders: closers.filter((c) => (c.skills[skill.slug] ?? 0) >= 1).length,
  }));
}

/** The skills of a closer the setter would care about: those in the catalog, by name. */
export function heldChips(
  closer: Pick<HandoffCloser, "skills">,
  skills: readonly HandoffSkill[],
): string[] {
  return skills.filter((s) => (closer.skills[s.slug] ?? 0) >= 1).map((s) => s.name);
}

export interface HandoffAsk {
  /** The caller's channel. */
  callId: string;
  note?: string;
  mode?: HandoffMode;
  /** A closer picked by name. */
  toSipEndpointId?: number | null;
  /** Skills on top of `closer` for "any closer"; ignored when a closer is named, since a pin
   *  bypasses skills (ADR-015) and the pick was the setter's judgement. */
  requiredSkills?: readonly string[];
}

/**
 * The body of `POST /handoffs` (ADR-016, ADR-031). `required_skills` is sent only when the setter
 * asked for a skill, so core-api's default — `closer` at level 1 — stands otherwise, and it always
 * carries `closer` too: a Spanish requirement alone would let a Spanish setter take the caller.
 */
export function handoffRequest(ask: HandoffAsk): Record<string, unknown> {
  const body: Record<string, unknown> = {
    call_id: ask.callId,
    note: ask.note?.trim() || null,
    mode: ask.mode ?? "blind",
  };
  if (ask.toSipEndpointId != null) {
    body.to_sip_endpoint_id = ask.toSipEndpointId;
  } else if (ask.requiredSkills?.length) {
    const slugs = [...new Set(["closer", ...ask.requiredSkills])];
    body.required_skills = slugs.map((slug) => ({ slug, min_level: 1 }));
  }
  return body;
}

/** What the two buttons do for the choice made, in the setter's words. */
export function sendHelp(target: string | null): string {
  if (target === null) {
    return (
      "A warm transfer keeps you talking until a closer picks up, then you brief them while the " +
      "caller holds. A blind transfer puts the caller on hold straight away."
    );
  }
  return (
    `A warm transfer rings only ${target}: if they do not pick up, you stay with the caller. ` +
    `A blind transfer puts the caller on hold for ${target} first, then for the next closer free.`
  );
}

/**
 * A consult to a named closer that ended because they could not be reached (ADR-031), as the
 * setter should hear it; null for a handoff that ended any other way, or has not ended.
 */
export function unreached(
  handoff: Pick<Handoff, "status" | "reason"> | undefined,
  name: string,
): { title: string; detail: string } | null {
  if (handoff?.status !== "cancelled") return null;
  const detail = "You are still on the call with the caller.";
  if (handoff.reason === "no_answer") return { title: `${name} did not pick up`, detail };
  if (handoff.reason === "unavailable") return { title: `${name} was not free`, detail };
  return null;
}
